import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Limbo() {
  const { user, currency, refresh } = useApp();
  const [searchParams] = useSearchParams();
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [target, setTarget] = useState(() => Number(searchParams.get("target")) || 2.00);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const winChance = Math.min(99, (97 / target).toFixed(4));

  const play = async () => {
    setBusy(true);
    try {
      const r = await api.post("/instant/limbo/play", {
        bet: Number(bet), currency, target: Number(target),
      });
      setResult(r.data);
      refresh();
      if (r.data.win) toast.success(`${r.data.result.toFixed(2)}x ≥ ${target}x · Won ${fmt(r.data.payout, currency)}`);
      else toast.error(`${r.data.result.toFixed(2)}x < ${target}x`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const resultColor = result ? (result.win ? "text-emerald-400" : "text-red-500") : "text-[#d4af37]";

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Limbo</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">Beat the target multiplier</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="lux-card p-8 flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-white/50 text-xs uppercase tracking-widest mb-2">Result</div>
          <div className={`font-serif text-8xl md:text-9xl ${resultColor}`}>
            {result ? result.result.toFixed(2) : (target).toFixed(2)}x
          </div>
          {result && (
            <div className={`mt-4 uppercase tracking-widest text-sm ${result.win ? "text-emerald-400" : "text-red-500"}`}>
              {result.win ? `HIT · +${fmt(result.payout, currency)}` : "MISS"}
            </div>
          )}
          {!result && <div className="text-white/40 text-xs mt-4">Set your target and play</div>}
        </div>

        <div className="lux-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Bet</label>
            <input data-testid="limbo-bet" type="number" value={bet} onChange={e=>setBet(e.target.value)}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 font-serif text-xl focus:border-[#d4af37]"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Target Multiplier</label>
            <input data-testid="limbo-target" type="number" min={1.01} step={0.01} value={target}
                   onChange={e=>setTarget(Number(e.target.value))}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 focus:border-[#d4af37]"/>
            <div className="flex gap-2 mt-2">
              {[1.5, 2, 5, 10, 50, 100].map(t=>(
                <button key={t} className="btn-ghost px-2 py-1 rounded text-xs" onClick={()=>setTarget(t)}>{t}x</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded bg-black/40 p-3">
              <div className="text-[10px] text-white/40 uppercase">Win Chance</div>
              <div className="text-[#d4af37] font-bold">{winChance}%</div>
            </div>
            <div className="rounded bg-black/40 p-3">
              <div className="text-[10px] text-white/40 uppercase">Payout</div>
              <div className="text-[#d4af37] font-bold">{Number(target).toFixed(2)}x</div>
            </div>
          </div>
          <div className="rounded bg-black/40 border border-white/10 p-3 text-xs text-white/50">
            Balance: <span className="text-[#d4af37] font-bold">{fmt(currency==="HUF"?user?.balance_huf:user?.balance_eur, currency)}</span>
          </div>
          <button data-testid="limbo-play" onClick={play} disabled={busy}
                  className="w-full btn-burgundy px-6 py-4 rounded-md text-lg uppercase tracking-widest">
            ▲ Play
          </button>
        </div>
      </div>
    </div>
  );
}
